import { Box, Typography } from '@mui/material';
import { Activity, CloudAlert, UserCheck, Users, type LucideIcon } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import type { AdminMetrics, MetricsPeriodKey } from '@/admin/adminApi';
import { Metric } from '@/ui/kit';
import { Sparkline } from '@/ui/charts';

import { dayLabel, periodShort, trendOf } from './dashboardModel';
import { ReadBlock } from './ReadBlock';
import type { AdminRead } from './useAdminRead';

/**
 * Les quatre tuiles du tableau de bord (deux sous 600 px : comptes actifs et
 * saisies).
 *
 * Chaque chiffre est celui du serveur. Trois choses sont **dites** plutot que
 * devinees :
 *
 * - **comptes actifs** : la definition est ecrite sous les tuiles, et il n'y a
 *   **ni ecart ni courbe** — le serveur rend `previous: null` et `series.activeAccounts:
 *   null` parce qu'une session ne garde que son dernier renouvellement, donc qu'un
 *   releve passe ne compterait que ceux qui ont cesse de venir ;
 * - **saisies recues** : date de reception du serveur, pas celle de la saisie ;
 * - **operations rejetees** : refusees par le serveur pour conflit de version.
 *   L'appareil a pu les renvoyer depuis, et le serveur ne le sait pas : ce n'est
 *   donc **pas** un nombre de problemes restant a traiter. L'ecart n'existe pas
 *   (aucune periode precedente servie) ; si le serveur en rend une, **une hausse
 *   est une mauvaise nouvelle** (`higherIsWorse`).
 *
 * La seule mini-courbe est celle des nouveaux comptes : c'est la seule serie
 * quotidienne honnete que le serveur fournit. Elle couvre 30 jours quelle que
 * soit la periode, et son nom accessible le dit.
 */

interface Tile {
  readonly id: string;
  readonly label: string;
  /** Intitule court sous 600 px, ou deux colonnes ne laissent pas la place du long. */
  readonly shortLabel: string;
  readonly icon: LucideIcon;
  readonly value: number;
  /** Periode precedente telle que le serveur la rend ; `null` : pas d'ecart. */
  readonly previous: number | null;
  /** Vrai quand une hausse est une mauvaise nouvelle : le ton de l'ecart s'inverse. */
  readonly higherIsWorse?: true;
  readonly caption: ReactNode;
  readonly chart?: ReactNode;
}

const tilesOf = (metrics: AdminMetrics, period: MetricsPeriodKey): readonly Tile[] => {
  const suffix = periodShort(period);
  const { accounts, entries, sync, series } = metrics;

  return [
    {
      id: 'active',
      label: `Comptes actifs · ${suffix}`,
      shortLabel: `Actifs · ${suffix}`,
      icon: Users,
      value: accounts.active.current,
      previous: accounts.active.previous,
      caption: 'Sessions ouvertes ou renouvelées',
    },
    {
      id: 'created',
      label: `Nouveaux comptes · ${suffix}`,
      shortLabel: `Nouveaux · ${suffix}`,
      icon: UserCheck,
      value: accounts.created.current,
      previous: accounts.created.previous,
      caption: `dont ${accounts.created.verified.toLocaleString('fr-FR')} vérifiés`,
      chart: (
        <Sparkline
          label="Nouveaux comptes par jour, 30 derniers jours"
          categories={series.days.map(dayLabel)}
          values={series.newAccounts}
          token="successText"
        />
      ),
    },
    {
      id: 'entries',
      label: `Saisies reçues · ${suffix}`,
      shortLabel: `Saisies · ${suffix}`,
      icon: Activity,
      value: entries.current,
      previous: entries.previous,
      caption: 'reçues par le serveur, tous types',
    },
    {
      id: 'conflicts',
      label: `Opérations rejetées · ${suffix}`,
      shortLabel: `Rejetées · ${suffix}`,
      icon: CloudAlert,
      value: sync.conflicts,
      previous: null,
      higherIsWorse: true,
      caption: `sur ${sync.received.toLocaleString('fr-FR')} opérations reçues`,
    },
  ];
};

/** Tuiles gardees sous 600 px : comptes actifs et saisies. */
const NARROW_TILES: readonly string[] = ['active', 'entries'];

export const DEFINITION_REJECTED_OPERATIONS =
  'Opérations rejetées : refusées par le serveur pour conflit de version ; l’appareil a pu les renvoyer depuis.';

export const DEFINITION_ACTIVE_ACCOUNTS =
  'Compte actif : session ouverte ou renouvelée dans la période, hors comptes d’administration et anonymisés. Aucune comparaison avec la période précédente : la mesure ne le permet pas honnêtement.';

export const MetricTiles = ({
  metrics,
  period,
  narrow,
  onRetry,
}: {
  readonly metrics: AdminRead<AdminMetrics>;
  readonly period: MetricsPeriodKey;
  readonly narrow: boolean;
  readonly onRetry: () => void;
}): ReactElement => (
  <Box component="section" aria-label="Indicateurs" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <ReadBlock state={metrics} label="Indicateurs" onRetry={onRetry} skeletonHeight={narrow ? 120 : 130}>
      {(data) => (
        <>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: narrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(220px, 1fr))',
            }}
          >
            {tilesOf(data, period)
              .filter((tile) => !narrow || NARROW_TILES.includes(tile.id))
              .map((tile) => ({ tile, trend: trendOf({ current: tile.value, previous: tile.previous, higherIsWorse: tile.higherIsWorse === true }) }))
              .map(({ tile, trend }) => (
                <Metric
                  key={tile.id}
                  label={narrow ? tile.shortLabel : tile.label}
                  icon={tile.icon}
                  value={tile.value}
                  caption={tile.caption}
                  {...(trend === null ? {} : { delta: trend })}
                  {...(tile.chart === undefined ? {} : { chart: tile.chart })}
                />
              ))}
          </Box>
          <Typography component="p" sx={{ fontSize: 12, lineHeight: '16px', color: 'tokens.textMuted' }}>
            {DEFINITION_ACTIVE_ACCOUNTS}
          </Typography>
          {narrow ? null : (
            <Typography component="p" sx={{ fontSize: 12, lineHeight: '16px', color: 'tokens.textMuted' }}>
              {DEFINITION_REJECTED_OPERATIONS}
            </Typography>
          )}
        </>
      )}
    </ReadBlock>
  </Box>
);
